import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseApiService } from '@core/api/base-api.service';
import {
  emptyMatrix,
  GrantPermissionPayload,
  PermissionDto,
  PermissionMatrix,
} from '../models/rbac.model';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly api = inject(BaseApiService);
  private readonly base = 'v1/admin/rbac/tenants';

  private mapMatrix(raw: any): PermissionMatrix {
    if (!raw) return emptyMatrix();
    return {
      member: Boolean(raw.member),
      create: Boolean(raw.create),
      read: Boolean(raw.read),
      update: Boolean(raw.update),
      delete: Boolean(raw.delete),
      analytics: Boolean(raw.analytics),
      permission: Boolean(raw.permission),
    };
  }

  private mapPermission(raw: any): PermissionDto {
    return {
      accountUuid: raw?.accountUuid ?? '',
      featureDetailName: raw?.featureDetailName ?? '',
      itemUuid: raw?.itemUuid ?? '',
      itemType: raw?.itemType ?? undefined,
      isOwner: Boolean(raw?.isOwner),
      matrix: this.mapMatrix(raw?.matrix),
      updatedAt: String(raw?.updatedAt ?? ''),
    };
  }

  getPermissions(tenantUuid: string, accountUuid?: string): Observable<readonly PermissionDto[]> {
    let params = new HttpParams();
    if (accountUuid) params = params.set('accountUuid', accountUuid);
    return this.api
      .get<readonly any[]>(`${this.base}/${tenantUuid}/permissions`, { params })
      .pipe(map((arr) => (Array.isArray(arr) ? arr : []).map((p) => this.mapPermission(p))));
  }

  grantPermission(tenantUuid: string, payload: GrantPermissionPayload): Observable<PermissionDto> {
    return this.api
      .post<any>(`${this.base}/${tenantUuid}/permissions`, payload)
      .pipe(map((p) => this.mapPermission(p)));
  }

  revokePermission(
    tenantUuid: string,
    accountUuid: string,
    featureDetailName: string,
    itemUuid: string,
  ): Observable<void> {
    return this.api
      .delete(
        `${this.base}/${tenantUuid}/permissions/${accountUuid}/${featureDetailName}/${itemUuid}`,
      )
      .pipe(map(() => void 0));
  }
}
